import { SimpleTable } from '@app/components/base/SimpleTable';
import { LDateTime } from '@app/components/date/LDateTime';
import { AuditMatch } from '@mi-front/core';
import { QueryView } from '@mi-front/ui';
import { Box, Typography } from '@mui/material';
import { UseQueryResult } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';
import {
  scheduleStatusDetailActionsMessage,
  scheduleStatusDetailMatchDatetimeMessage,
  scheduleStatusDetailMatchedScheduleMessage,
  scheduleStatusDetailNoMatchedScheduleMessage,
  scheduleStatusDetailUnmatchedMatchesMessage,
} from './schedule-status-spot-messages';
import { getUnmatchedMatches } from './schedule-status-spot-utils';
import { ScheduleStatusSpotDetailMatchActions } from './ScheduleStatusSpotDetailMatchActions';

interface ScheduleStatusSpotDetailUnmatchedMatchesProps {
  matchesQuery: UseQueryResult<readonly AuditMatch[], unknown>;
}

/**
 * Airings that did not confirm against any schedule. Hidden when every match is linked.
 */
export function ScheduleStatusSpotDetailUnmatchedMatches({
  matchesQuery,
}: ScheduleStatusSpotDetailUnmatchedMatchesProps) {
  return (
    <QueryView query={matchesQuery} inline noLoader>
      {(matches) => {
        const unmatched = getUnmatchedMatches(matches ?? []).sort((a, b) =>
          a.datetime.localeCompare(b.datetime),
        );

        if (unmatched.length === 0) {
          return <></>;
        }

        return (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              <FormattedMessage {...scheduleStatusDetailUnmatchedMatchesMessage} />
            </Typography>
            <SimpleTable
              columns={[
                {
                  header: {
                    content: <FormattedMessage {...scheduleStatusDetailMatchDatetimeMessage} />,
                  },
                  headerProps: { sx: { width: '45%' } },
                  renderCell: (match) => ({
                    content: <LDateTime value={match.datetime} showRelative />,
                  }),
                },
                {
                  header: {
                    content: <FormattedMessage {...scheduleStatusDetailMatchedScheduleMessage} />,
                  },
                  headerProps: { align: 'center', sx: { width: '40%' } },
                  renderCell: () => ({
                    content: (
                      <Typography variant="body2" color="text.secondary">
                        <FormattedMessage {...scheduleStatusDetailNoMatchedScheduleMessage} />
                      </Typography>
                    ),
                    props: { align: 'center' },
                  }),
                },
                {
                  header: {
                    content: <FormattedMessage {...scheduleStatusDetailActionsMessage} />,
                  },
                  headerProps: { align: 'center', sx: { width: '15%', whiteSpace: 'nowrap' } },
                  renderCell: (match) => ({
                    content: <ScheduleStatusSpotDetailMatchActions match={match} />,
                    props: { align: 'center' },
                  }),
                },
              ]}
              data={unmatched}
            />
          </Box>
        );
      }}
    </QueryView>
  );
}
